import { useCallback, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Bold, Italic, List, ListOrdered, Link as LinkIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
}

type Tab = "edit" | "preview";

/**
 * Minimal markdown editor: a textarea with a small selection-aware toolbar
 * (bold / italic / bullet list / numbered list / link) plus a tabbed live
 * preview rendered by react-markdown. Same API as the old `@mdxeditor`
 * component but no transitive prismjs / @lexical/code dependency (which
 * Vite 8's rolldown kept tree-shaking, leaving `Prism is not defined` in prod).
 */
export function MarkdownEditor({ value, onChange }: MarkdownEditorProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [tab, setTab] = useState<Tab>("edit");

  /**
   * Wrap a selection with `before`/`after` (e.g. "**","**" for bold). When
   * `range` is omitted we read the live selection; callers that show a modal
   * (e.g. `window.prompt`) must snapshot the selection BEFORE the prompt and
   * pass it explicitly — prompts blur the textarea, collapsing the live
   * selection to 0,0 and inserting at the wrong place.
   *
   * If the range is empty we insert `placeholder` between the markers so the
   * user can start typing immediately. The textarea is refocused with the
   * inserted text selected so subsequent toolbar clicks chain naturally.
   */
  const wrap = useCallback(
    (before: string, after: string, placeholder = "", range?: { start: number; end: number }) => {
      const ta = ref.current;
      if (!ta) return;
      const s = range?.start ?? ta.selectionStart;
      const e = range?.end ?? ta.selectionEnd;
      const v = ta.value;
      const selected = v.slice(s, e) || placeholder;
      const next = v.slice(0, s) + before + selected + after + v.slice(e);
      onChange(next);
      // Restore selection on the inserted text after React commits the new value.
      requestAnimationFrame(() => {
        const target = ref.current;
        if (!target) return;
        const start = s + before.length;
        target.focus();
        target.setSelectionRange(start, start + selected.length);
      });
    },
    [onChange],
  );

  /**
   * Prefix each selected line with `marker` (used for `- ` and `1. ` lists).
   * Operating per-line keeps multi-line list creation predictable.
   */
  const linePrefix = useCallback(
    (marker: string | ((index: number) => string)) => {
      const ta = ref.current;
      if (!ta) return;
      const { selectionStart: s, selectionEnd: e, value: v } = ta;
      const lineStart = v.lastIndexOf("\n", s - 1) + 1;
      const lineEnd = e === s ? (v.indexOf("\n", e) === -1 ? v.length : v.indexOf("\n", e)) : e;
      const block = v.slice(lineStart, lineEnd);
      const prefixed = block
        .split("\n")
        .map((line, i) => (typeof marker === "function" ? marker(i) : marker) + line)
        .join("\n");
      const next = v.slice(0, lineStart) + prefixed + v.slice(lineEnd);
      onChange(next);
      requestAnimationFrame(() => {
        const target = ref.current;
        if (!target) return;
        target.focus();
        target.setSelectionRange(lineStart, lineStart + prefixed.length);
      });
    },
    [onChange],
  );

  const insertLink = useCallback(() => {
    // Snapshot the selection BEFORE `window.prompt` — opening the prompt
    // blurs the textarea and collapses its selection to 0, which would
    // otherwise drop the link at the very start of the description.
    const ta = ref.current;
    const range = ta ? { start: ta.selectionStart, end: ta.selectionEnd } : undefined;
    const url = window.prompt("URL:");
    if (!url) return;
    wrap("[", `](${url})`, "tekst linku", range);
  }, [wrap]);

  return (
    <div className="overflow-hidden rounded-md border border-input">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 border-b border-input bg-muted/40 px-1.5 py-1">
        <ToolbarButton label="Pogrubienie" onClick={() => wrap("**", "**", "tekst")}>
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton label="Kursywa" onClick={() => wrap("_", "_", "tekst")}>
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarSeparator />
        <ToolbarButton label="Lista punktowana" onClick={() => linePrefix("- ")}>
          <List className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton label="Lista numerowana" onClick={() => linePrefix((i) => `${i + 1}. `)}>
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarSeparator />
        <ToolbarButton label="Link" onClick={insertLink}>
          <LinkIcon className="h-3.5 w-3.5" />
        </ToolbarButton>

        <div className="ml-auto flex items-center gap-0.5">
          <TabButton active={tab === "edit"} onClick={() => setTab("edit")}>
            Edytuj
          </TabButton>
          <TabButton active={tab === "preview"} onClick={() => setTab("preview")}>
            Podgląd
          </TabButton>
        </div>
      </div>

      {/* Body */}
      {tab === "edit" ? (
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="block min-h-[120px] w-full resize-y bg-background px-3 py-2 text-sm leading-relaxed outline-none placeholder:text-muted-foreground"
          placeholder="Opis wydarzenia (markdown)"
        />
      ) : (
        <div className="prose prose-sm min-h-[120px] max-w-none px-3 py-2 prose-headings:font-brand prose-a:text-pulso-primary [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6">
          {value.trim() ? (
            <ReactMarkdown>{value}</ReactMarkdown>
          ) : (
            <p className="text-sm text-muted-foreground">Brak treści.</p>
          )}
        </div>
      )}
    </div>
  );
}

function ToolbarButton({
  children,
  label,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="inline-flex h-7 w-7 items-center justify-center rounded text-pulso-muted transition-colors hover:bg-black/5 hover:text-foreground"
    >
      {children}
    </button>
  );
}

function ToolbarSeparator() {
  return <span aria-hidden="true" className="mx-1 h-4 w-px bg-black/10" />;
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-7 rounded px-2 text-xs font-medium transition-colors",
        active ? "bg-white text-foreground shadow-sm" : "text-pulso-muted hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}
